import { useTranslation } from 'react-i18next';
import { AppLayout } from '@/components/layout/AppLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { ActivityList } from '@/components/activities/ActivityList';
import { GarminAttribution } from '@/components/activities/GarminAttribution';
import { AppleHealthAttribution } from '@/components/activities/AppleHealthAttribution';
import { fetchActivities, syncActivitiesNow, getIntegrationsStatus, syncAppleHealthActivities } from '@/lib/api';
import type { AppleHealthSyncPayload } from '@/lib/api';
import { isHealthKitPlatform, fetchHealthKitWorkouts } from '@/lib/healthkit';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedQuery } from '@/hooks/useAuthenticatedQuery';
import { Loader2, RefreshCw, Plus } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useMemo, useState, useEffect, useRef } from 'react';
import { format, parseISO, subDays } from 'date-fns';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { useAutoMatchSessions } from '@/hooks/useAutoMatchSessions';
import { useSyncTodayWorkout } from '@/hooks/useSyncTodayWorkout';
import { PlanCoachChat } from '@/components/plan/PlanCoachChat';
import { RecordActivityDialog } from '@/components/activities/RecordActivityDialog';
import { useAuth } from '@/context/AuthContext';
import { log } from '@/lib/logger';

export default function Activities() {
  useSyncTodayWorkout();
  const { t } = useTranslation();
  const { user } = useAuth();
  const stravaConnected = user?.strava_connected ?? false;
  const [searchParams] = useSearchParams();
  const activityId = searchParams.get('activity');
  const queryClient = useQueryClient();
  const [isSyncing, setIsSyncing] = useState(false);
  const [recordDialogOpen, setRecordDialogOpen] = useState(false);
  
  // Auto-match activities to planned sessions
  useAutoMatchSessions(true);
  
  const { data: activities, isLoading, error, refetch } = useAuthenticatedQuery({
    queryKey: ['activities', 'limit', 100],
    queryFn: () => fetchActivities({ limit: 100 }),
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes - activities don't change that frequently
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    // Only refetch when window regains focus (user comes back to tab)
    refetchOnWindowFocus: true,
    // Don't auto-refetch in background - too expensive
    refetchInterval: false,
  });

  const { data: integrationsStatus } = useAuthenticatedQuery({
    queryKey: ['integrations', 'status'],
    queryFn: getIntegrationsStatus,
    staleTime: 2 * 60 * 1000,
  });
  const garminConnected = integrationsStatus?.integrations?.some(
    (i: { provider: string; connected: boolean }) => i.provider === 'garmin' && i.connected
  ) ?? false;
  const appleHealthConnected = integrationsStatus?.integrations?.some(
    (i: { provider: string; connected: boolean }) => i.provider === 'apple_health' && i.connected
  ) ?? false;

  // Check for missing recent activities
  const missingRecentActivities = useMemo(() => {
    // Ensure activities is an array
    const activitiesArray = Array.isArray(activities) ? activities : [];
    if (activitiesArray.length === 0) return null;
    
    // Sort activities by date (most recent first)
    const sortedActivities = [...activitiesArray].sort((a, b) => {
      const dateA = a.date ? (typeof a.date === 'string' ? parseISO(a.date.split('T')[0]) : new Date(a.date)) : new Date(0);
      const dateB = b.date ? (typeof b.date === 'string' ? parseISO(b.date.split('T')[0]) : new Date(b.date)) : new Date(0);
      return dateB.getTime() - dateA.getTime();
    });
    
    const today = new Date();
    const todayStr = format(today, 'yyyy-MM-dd');
    const yesterdayStr = format(subDays(today, 1), 'yyyy-MM-dd');
    
    // Check if we have today's activity
    const hasToday = sortedActivities.some(act => {
      if (!act.date) return false;
      const actDateStr = act.date.split('T')[0];
      return actDateStr === todayStr;
    });
    
    // Check if we have yesterday's activity
    const hasYesterday = sortedActivities.some(act => {
      if (!act.date) return false;
      const actDateStr = act.date.split('T')[0];
      return actDateStr === yesterdayStr;
    });
    
    const missing = [];
    if (!hasToday) {
      missing.push('today');
    }
    if (!hasYesterday && sortedActivities.length > 0) {
      // Only warn about yesterday if we have other activities (might be new user)
      const oldestActivity = sortedActivities[sortedActivities.length - 1];
      if (oldestActivity?.date) {
        const oldestDateStr = oldestActivity.date.split('T')[0];
        const oldestDate = parseISO(oldestDateStr);
        // Only warn if oldest activity is older than yesterday (user has been active before)
        if (oldestDate < subDays(today, 2)) {
          missing.push('yesterday');
        }
      }
    }
    
    return missing.length > 0 ? missing : null;
  }, [activities]);

  // Track if we've already logged missing activities to prevent loop
  const hasLoggedMissing = useRef(false);
  const lastActivitiesCount = useRef<number | null>(null);
  const lastMissingActivities = useRef<string[] | null>(null);

  // Debug logging - only log when values actually change
  useEffect(() => {
    if (activities) {
      const activitiesCount = activities.length;
      const missingStr = missingRecentActivities ? JSON.stringify(missingRecentActivities) : null;
      
      // Only log if activities count changed or missing activities changed
      if (activitiesCount !== lastActivitiesCount.current) {
        log.debug('ACTIVITIES', 'Loaded activities:', activitiesCount);
        lastActivitiesCount.current = activitiesCount;
        hasLoggedMissing.current = false; // Reset when activities change
      }
      
      // Only log missing activities once when first detected
      if (missingRecentActivities && !hasLoggedMissing.current && missingStr !== (lastMissingActivities.current ? JSON.stringify(lastMissingActivities.current) : null)) {
        log.warn('ACTIVITIES', 'Missing recent activities:', missingRecentActivities);
        hasLoggedMissing.current = true;
        lastMissingActivities.current = missingRecentActivities;
      } else if (!missingRecentActivities) {
        // Reset flag when no activities are missing
        hasLoggedMissing.current = false;
        lastMissingActivities.current = null;
      }
    }
    
    if (error) {
      log.error('ACTIVITIES', 'Error loading activities:', error);
    }
  }, [activities, missingRecentActivities, error]);

  const anyProviderConnected = stravaConnected || garminConnected || appleHealthConnected;

  const handleSync = async () => {
    if (!anyProviderConnected) {
      toast({
        title: t('activities.syncFailedTitle'),
        description: t('activities.connectStravaToSync'),
        variant: 'destructive',
      });
      return;
    }
    setIsSyncing(true);
    try {
      // Apple Health: fetch from device and push to backend (same as IntegrationsSection)
      if (appleHealthConnected && isHealthKitPlatform()) {
        const now = new Date();
        const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        const workouts = await fetchHealthKitWorkouts(thirtyDaysAgo, now);
        if (workouts.length === 0) {
          toast({ title: 'Apple Health', description: t('settings.integrations.appleHealthNoWorkouts') });
        } else {
          const payload: AppleHealthSyncPayload[] = workouts.map(w => ({
            id: w.id,
            sport: w.sport,
            start_date: w.startDate,
            end_date: w.endDate,
            duration_seconds: w.durationSeconds,
            distance_meters: w.distanceMeters ?? null,
            calories: w.calories ?? null,
            avg_heart_rate: w.avgHeartRate ?? null,
            max_heart_rate: w.maxHeartRate ?? null,
            source_name: w.sourceName ?? null,
            source_bundle_id: w.sourceBundleId ?? null,
          }));
          const ahResult = await syncAppleHealthActivities(payload);
          toast({
            title: 'Apple Health',
            description: t('settings.integrations.appleHealthImported', { count: ahResult.imported }),
          });
        }
      }

      // Garmin / Strava: trigger server-side sync
      if (garminConnected || stravaConnected) {
        await syncActivitiesNow();
        toast({
          title: t('activities.syncStartedTitle'),
          description: t('activities.syncStartedDesc'),
        });
      }

      // Refresh activity list after sync
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['activities'] });
        refetch();
      }, appleHealthConnected && isHealthKitPlatform() ? 1000 : 3000);
    } catch (err) {
      log.error('ACTIVITIES', 'Failed to sync activities:', err);
      toast({
        title: t('activities.syncFailedTitle'),
        description: err instanceof Error ? err.message : t('activities.syncFailedDesc'),
        variant: 'destructive',
      });
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <AppLayout>
      <div className="flex flex-col h-full min-h-screen bg-transparent">
        <PageHeader
          title={t('nav.history')}
          subtitle={t('activities.subtitle')}
          statusLabel={t('activities.statusLabel')}
          actions={
            <div className="flex flex-col items-end gap-1">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRecordDialogOpen(true)}
                  className="border-[rgba(0,255,200,0.2)] text-[#00FFC8] hover:bg-[rgba(0,255,200,0.08)] text-[10px] font-mono uppercase tracking-wider"
                >
                  <Plus className="h-3.5 w-3.5 mr-1.5" />
                  Record
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSync}
                  disabled={isLoading || isSyncing || !anyProviderConnected}
                  title={!anyProviderConnected ? t('activities.connectStravaToSync') : undefined}
                  className="border-white/[0.08] text-slate-400 hover:text-slate-200 text-[10px] font-mono uppercase tracking-wider"
                >
                  <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${isSyncing ? 'animate-spin' : ''}`} />
                  {isSyncing ? t('activities.syncingButton') : t('activities.syncButton')}
                </Button>
              </div>
              <p className="text-[10px] font-mono text-slate-600">
                {t('activities.syncNote')}
              </p>
            </div>
          }
        />

        {/* Content */}
        <div className="flex-1 min-h-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12 rounded-xl border border-border/50 backdrop-blur-sm">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="text-center py-12 text-muted-foreground rounded-xl border border-border/50 backdrop-blur-sm">
              <p>{t('activities.loadError')}</p>
              <p className="text-xs mt-2">
                {error instanceof Error ? error.message : 'Unknown error occurred'}
              </p>
            </div>
          ) : (
            <>
              <ActivityList activities={activities || []} initialExpandedId={activityId || null} />
              {garminConnected && (
                <div className="mt-4 pt-4 border-t border-border/50">
                  <GarminAttribution />
                </div>
              )}
              {appleHealthConnected && (
                <div className="mt-4 pt-4 border-t border-border/50">
                  <AppleHealthAttribution />
                </div>
              )}
            </>
          )}
        </div>

        {/* Floating Coach Chat */}
        <PlanCoachChat />
      </div>

      <RecordActivityDialog open={recordDialogOpen} onOpenChange={setRecordDialogOpen} />
    </AppLayout>
  );
}
